import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { wishlistAPI } from '../services/wishlistAPI';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};

// Always read current auth from sessionStorage - avoids stale state after login
const checkAuth = () => !!sessionStorage.getItem('authToken');

export const WishlistProvider = ({ children }) => {
  const [wishlistIds, setWishlistIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const isAuthenticated = checkAuth();

  const fetchWishlistIds = useCallback(async () => {
    if (!checkAuth()) {
      setWishlistIds([]);
      return;
    }
    try {
      const response = await wishlistAPI.getWishlistIds();
      if (response.success) {
        setWishlistIds(response.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch wishlist:', err);
      setWishlistIds([]);
    }
  }, []);

  useEffect(() => {
    fetchWishlistIds();
  }, [fetchWishlistIds]);

  const toggleWishlist = useCallback(async (propertyId) => {
    if (!checkAuth()) return { success: false, needsLogin: true };
    try {
      setLoading(true);
      const response = await wishlistAPI.toggleWishlist(propertyId);
      if (response.success) {
        const ids = response.data?.wishlist?.map(id => id.toString?.() || id) || [];
        setWishlistIds(ids);
        return { success: true, action: response.action };
      }
      return { success: false };
    } catch (err) {
      console.error('Toggle wishlist error:', err);
      return { success: false, error: err.response?.data?.message || err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const isInWishlist = useCallback((propertyId) => {
    return wishlistIds.includes(propertyId?.toString());
  }, [wishlistIds]);

  const value = {
    wishlistIds,
    isInWishlist,
    toggleWishlist,
    fetchWishlistIds,
    loading,
    isAuthenticated,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
